import { Box, SimpleGrid, Flex, Spinner, useToast } from "@chakra-ui/react";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Card from "components/card/Card.js";
import ReportFilters from "./components/ReportFilters";
import ReportStatCards from "./components/ReportStatCards";
import PublishingTrendChart from "./components/PublishingTrendChart";
import BroadcastTrendChart from "./components/BroadcastTrendChart";
import ViewsByCategoryChart from "./components/ViewsByCategoryChart";
import TopNewsTable from "./components/TopNewsTable";
import BroadcastStatsPanel from "./components/BroadcastStatsPanel";
import ContentOverviewPanel from "./components/ContentOverviewPanel";
import {
  getOverview,
  getTopNews,
  getViewsByCategory,
  getPublishingTrend,
  getBroadcastStats,
  getBroadcastTrend,
  getContentOverview,
} from "./reportsApi";

const DAY_MS = 24 * 60 * 60 * 1000;
const toDateInputValue = (date) => date.toISOString().slice(0, 10);

export default function MainDashboard() {
  const { t } = useTranslation();
  const toast = useToast();

  const [from, setFrom] = useState(() => toDateInputValue(new Date(Date.now() - 30 * DAY_MS)));
  const [to, setTo] = useState(() => toDateInputValue(new Date()));
  const [interval, setInterval] = useState("day");

  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [topNews, setTopNews] = useState([]);
  const [viewsByCategory, setViewsByCategory] = useState([]);
  const [publishingTrend, setPublishingTrend] = useState([]);
  const [broadcastStats, setBroadcastStats] = useState(null);
  const [broadcastTrend, setBroadcastTrend] = useState([]);
  const [contentOverview, setContentOverview] = useState(null);

  const range = { from, to };

  const fetchRangedReports = useCallback(async () => {
    setLoading(true);
    try {
      const [overviewData, topNewsData, viewsByCategoryData, publishingTrendData, broadcastStatsData, broadcastTrendData] =
        await Promise.all([
          getOverview(range),
          getTopNews({ ...range, limit: 10 }),
          getViewsByCategory(range),
          getPublishingTrend({ ...range, interval }),
          getBroadcastStats(range),
          getBroadcastTrend({ ...range, interval }),
        ]);
      setOverview(overviewData);
      setTopNews(topNewsData);
      setViewsByCategory(viewsByCategoryData);
      setPublishingTrend(publishingTrendData);
      setBroadcastStats(broadcastStatsData);
      setBroadcastTrend(broadcastTrendData);
    } catch (error) {
      toast({
        title: t("An error occurred."),
        description: error.message || t("Failed to fetch reports."),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, interval, t, toast]);

  useEffect(() => {
    fetchRangedReports();
  }, [fetchRangedReports]);

  // All-time, not affected by the date-range filter — fetched once.
  useEffect(() => {
    getContentOverview()
      .then(setContentOverview)
      .catch((error) => {
        toast({
          title: t("An error occurred."),
          description: error.message || t("Failed to fetch content overview."),
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top-right",
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <Card w="100%" p="20px" mb="20px">
        <ReportFilters
          from={from}
          to={to}
          onFromChange={setFrom}
          onToChange={setTo}
          interval={interval}
          onIntervalChange={setInterval}
        />
      </Card>

      {loading && !overview ? (
        <Flex justify="center" align="center" minH="200px">
          <Spinner size="xl" />
        </Flex>
      ) : (
        <>
          <ReportStatCards overview={overview} />

          <Box mb="20px">
            <PublishingTrendChart rows={publishingTrend} interval={interval} />
          </Box>

          <SimpleGrid columns={{ base: 1, xl: 2 }} gap="20px" mb="20px">
            <ViewsByCategoryChart rows={viewsByCategory} />
            <BroadcastTrendChart rows={broadcastTrend} interval={interval} />
          </SimpleGrid>

          <Box mb="20px">
            <TopNewsTable rows={topNews} />
          </Box>

          <Box mb="20px">
            <BroadcastStatsPanel stats={broadcastStats} />
          </Box>

          <Box mb="20px">
            <ContentOverviewPanel overview={contentOverview} />
          </Box>
        </>
      )}
    </Box>
  );
}
